/**
 * EventHandler - Handles all user interactions with the timeline
 * Manages dragging, resizing, clicking, and other user events
 */
class EventHandler {
  /**
   * Create an EventHandler
   * @param {TimelineCalendar} timeline - The timeline instance
   */
  constructor(timeline) {
    this.timeline = timeline;
    this.isDragging = false;
    this.isResizing = false;
    this.isDraggingMarker = false;
    this.draggedBlock = null;
    this.draggedMarker = null;
    this.resizeHandle = null;
    this.startX = 0;
    this.startY = 0;
    this.initialBlockStates = new Map();
    this.initialMarkerState = null;

    this.mouseUpHandler = this.handleMouseUp.bind(this);
    this.mouseMoveHandler = this.handleMouseMove.bind(this);

    this.lastMovement = {
      dx: 0,
      isSnapped: false
    };

    this.debounceFlags = {
      scroll: false,
      drag: false,
      resize: false
    };

    this.throttledUpdateConnections = this.throttle(() => {
      this.timeline.connectionManager.updateConnections();
    }, 30);
  }

  /**
   * Setup event listeners for the timeline
   */
  setupEventListeners() {
    window.addEventListener('resize', this.debounce(() => {
      this.timeline.connectionManager.updateConnections();
    }, 100));

    this.timeline.timelineContainer?.addEventListener('scroll', () => {
      if (this.debounceFlags.scroll) return;
      this.debounceFlags.scroll = true;

      requestAnimationFrame(() => {
        this.timeline.connectionManager.updateConnections();
        if (this.timeline.headerArea) {
          this.timeline.headerArea.scrollTop = this.timeline.timelineContainer.scrollTop;
        }

        this.debounceFlags.scroll = false;
      });
    });
  }

  /**
   * Handle click on a day column
   * @param {Event} e - Click event
   * @param {number} day - Day number
   * @param {number} rowIndex - Row index
   */
  async handleDayClick(e, day, rowIndex) {
    if (!this.timeline.isClickToAddMode) {
      return;
    }

    if (this.timeline.connectionManager.isConnectionMode || this.timeline.markerManager.isMarkerMode) return;

    const displayDay = day;
    day = day - 1;

    if (day < 0 || day >= this.timeline.headers.length) {
      this.timeline.showNotification(
        this.timeline.t('Invalid day value: {day}, valid range is 1-{max}',
          {day: displayDay, max: this.timeline.headers.length}),
        'warning'
      );
      day = Math.max(0, Math.min(day, this.timeline.headers.length - 1));
    }

    const taskInfo = this.timeline.dataManager.getTaskInfoByRowIndex(rowIndex);
    const defaultDuration = this.timeline.options.defaultBlockDuration || 1;

    const startDate = this.timeline.workingDaysManager.markerDayToDate(day);

    const tempId = Date.now();

    const newBlock = {
      id: 0,
      tempId: tempId,
      rowIndex: rowIndex,
      startDate: startDate,
      endDate: this.timeline.dataManager.calculateEndDate(startDate, defaultDuration),
      duration: defaultDuration,
      color: this.timeline.currentColor,
      displayText: taskInfo ? taskInfo.displayText : '',
      icon: taskInfo ? taskInfo.icon : null,
      progress: 0,
      isNew: true
    };

    if (this.timeline.options.preventOverlap) {
      const overlappingBlock = this.timeline.dataManager.checkBlockDateOverlap(newBlock);
      if (overlappingBlock) {
        this.timeline.showNotification(this.timeline.t('Cannot add block: Not enough space'), 'error');
        return false;
      }
    }

    try {
      this.timeline.dataManager.blocks.push(newBlock);
      this.timeline.dataManager.updateBlockDates(newBlock);

      this.timeline.domRenderer.renderBlock(newBlock, null);
      this.timeline.connectionManager.updateConnections();

      this.timeline.showNotification(this.timeline.t('Creating block...'), 'info', 1000);

      const result = await this.createNewBlockOnServer(newBlock);

      if (result.success) {
        const serverId = result.data.data.id;

        newBlock.id = serverId;
        newBlock.isNew = false;

        const blockElement = document.querySelector(`.task-block[data-id="${tempId}"]`);
        if (blockElement) {
          blockElement.dataset.id = serverId;
        }
        this.timeline.showNotification(this.timeline.t('Block created successfully'), 'success');

        this.timeline.triggerCallback('onBlockCreate', newBlock);
        this.timeline.triggerDataChangeCallback();

      } else {
        this.removeFailedBlock(newBlock);
        this.timeline.showNotification(
          this.timeline.t('Failed to create block: {message}', {message: result.error || 'Unknown error'}),
          'error'
        );
      }

    } catch (error) {
      console.error('Error creating block:', error);
      this.removeFailedBlock(newBlock);
      this.timeline.showNotification(this.timeline.t('Error creating block'), 'error');
    }
  }

  /**
   * สร้างบล็อกใหม่บน server
   * @param {Object} block - ข้อมูลบล็อกใหม่
   * @returns {Promise<Object>} - ผลลัพธ์จาก API
   */
  async createNewBlockOnServer(block) {
    const blockData = {
      tempId: block.tempId,
      row_index: block.rowIndex,
      start_date: block.startDate,
      end_date: block.endDate,
      duration: block.duration,
      color: block.color,
      display_text: block.displayText || '',
      icon: block.icon || '',
      progress: block.progress || 0
    };

    return await window.apiHandler.createBlock(blockData);
  }

  /**
   * ลบบล็อกที่สร้างไม่สำเร็จ
   * @param {Object} block - บล็อกที่ต้องการลบ
   */
  removeFailedBlock(block) {
    this.timeline.dataManager.blocks = this.timeline.dataManager.blocks.filter(
      b => b.tempId !== block.tempId
    );

    const blockElement = document.querySelector(`.task-block[data-id="${block.tempId}"]`);
    if (blockElement) {
      blockElement.remove();
    }

    this.timeline.connectionManager.updateConnections();
  }

  /**
   * Handle mouse down on a block for dragging
   * @param {MouseEvent} e - Mouse event
   * @param {Object} block - Block data
   */
  handleBlockMouseDown(e, block) {
    if (this.timeline.connectionManager.isConnectionMode) {
      this.timeline.connectionManager.handleConnectionClick(block, e.target);
      return;
    }

    if (this.timeline.markerManager.isMarkerMode) {
      return;
    }

    if (e.target.classList.contains('resize-handle')) {
      return;
    }

    this.startInteraction(e, block, 'drag');

    e.preventDefault();
    e.stopPropagation();
  }

  calculateNewPosition(deltaX, originalLeft, draggedBlock) {
    const dayWidth = this.timeline.options.itemWidth;
    let newLeft = originalLeft + deltaX;

    const newDate = this.timeline.workingDaysManager.positionToDate(newLeft, dayWidth);
    if (newDate) {
      newLeft = this.timeline.workingDaysManager.dateToPosition(newDate, dayWidth);
    }

    const timelineWidth = this.timeline.headers.length * dayWidth;
    const blockWidth = draggedBlock.width;

    const leftLimit = -(blockWidth - dayWidth);
    const rightLimit = timelineWidth - dayWidth;

    return Math.max(leftLimit, Math.min(newLeft, rightLimit));
  }

  calculateNewRow(deltaY, originalRowIndex) {
    if (!this.timeline.options.allowRowCrossing) {
      return originalRowIndex;
    }

    const taskRows = document.querySelectorAll('.task-row');
    if (taskRows.length === 0) return originalRowIndex;

    const rowHeight = taskRows[0]?.offsetHeight || 60;
    const rowMove = Math.round(deltaY / rowHeight);
    let newRowIndex = originalRowIndex + rowMove;

    newRowIndex = Math.max(0, Math.min(newRowIndex, taskRows.length - 1));

    return newRowIndex;
  }

  checkForOverlap(tempBlock) {
    if (!this.timeline.options.preventOverlap) {
      return false;
    }

    const testBlock = {
      ...tempBlock,
      endDate: this.timeline.workingDaysManager.calculateEndDate(
        tempBlock.startDate,
        tempBlock.duration
      )
    };

    return this.timeline.dataManager.checkBlockDateOverlap(testBlock);
  }

  updateBlockElementDuringDrag(blockElement, newLeft, newRowIndex) {
    if (!blockElement) return;

    blockElement.style.left = `${newLeft}px`;
    blockElement.style.transform = 'translateZ(0)';

    if (newRowIndex !== parseInt(blockElement.dataset.rowIndex)) {
      const newTaskRow = document.querySelector(`.task-row[data-row-index="${newRowIndex}"]`);
      if (newTaskRow) {
        const newTaskTimeline = newTaskRow.querySelector('.task-timeline');
        if (newTaskTimeline && blockElement.parentNode !== newTaskTimeline) {
          newTaskTimeline.appendChild(blockElement);
          blockElement.dataset.rowIndex = newRowIndex;
        }
      }
    }

    blockElement.classList.add('dragging');
  }

  /**
   * Handle mouse down on a resize handle
   * @param {MouseEvent} e - Mouse event
   * @param {Object} block - Block data
   * @param {string} direction - Resize direction ('left' or 'right')
   */
  handleResizeHandleMouseDown(e, block, direction) {
    this.startInteraction(e, block, 'resize', direction);
  }

  /**
   * Start an interaction (drag, resize, or marker movement)
   * @param {MouseEvent} e - Mouse event
   * @param {Object} blockOrMarker - Block or marker data
   * @param {string} mode - Interaction mode ('drag', 'resize', or 'marker')
   * @param {string|null} direction - Resize direction (for resize mode)
   */
  startInteraction(e, blockOrMarker, mode, direction = null) {
    if (mode === 'drag') {
      this.isDragging = true;
      this.draggedBlock = blockOrMarker;
      this.timeline.dataManager.updateBlockDates(blockOrMarker);
    } else if (mode === 'resize') {
      this.isResizing = true;
      this.draggedBlock = blockOrMarker;
      this.resizeHandle = direction ? {block: blockOrMarker, direction} : null;
    } else if (mode === 'marker') {
      this.isDraggingMarker = true;
      this.draggedMarker = blockOrMarker;
      this.initialMarkerState = {
        day: blockOrMarker.day,
        rowIndex: blockOrMarker.rowIndex || 0
      };
    }

    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;

    this.startX = clientX;
    this.startY = clientY;

    if (this.isDragging) {
      this.storeBlockInitialState(blockOrMarker);
    } else if (this.isResizing) {
      this.storeBlockInitialState(blockOrMarker);

      document.body.style.cursor = direction === 'left' ? 'w-resize' : 'e-resize';
      const blockElement = document.querySelector(`.task-block[data-id="${blockOrMarker.id}"]`);
      if (blockElement) blockElement.classList.add('resizing');
    } else if (this.isDraggingMarker) {
      document.body.style.cursor = 'move';
    }

    document.addEventListener('mousemove', this.mouseMoveHandler);
    document.addEventListener('mouseup', this.mouseUpHandler);
    document.addEventListener('touchmove', this.handleTouchMove.bind(this), {passive: false});
    document.addEventListener('touchend', this.mouseUpHandler, {passive: false});

    e.preventDefault();
    e.stopPropagation();
  }

  /**
   * Store the initial state of a block before interaction
   * @param {Object} block - Block data
   */
  storeBlockInitialState(block) {
    if (!block) return;

    const blockId = block.id || block.tempId;

    if (!blockId) {
      console.warn(this.timeline.t('Block has no ID or tempId'), block);
      return;
    }

    const startDay = block.startDate ? this.timeline.workingDaysManager.dateToIndex(block.startDate) : 0;

    this.initialBlockStates.set(blockId, {
      left: block.left || 0,
      width: block.width || (block.duration * this.timeline.options.itemWidth),
      rowIndex: block.rowIndex || 0,
      startDay: startDay,
      startDate: block.startDate || '',
      endDate: block.endDate || '',
      duration: block.duration || 1,
      progress: block.progress || 0
    });
  }

  /**
   * Handle mouse move during an interaction
   * @param {MouseEvent} e - Mouse event
   */
  handleMouseMove(e) {
    if (this.isDragging) {
      if (this.debounceFlags.drag) return;
      this.debounceFlags.drag = true;
      requestAnimationFrame(() => {
        this.handleDragging(e.clientX, e.clientY);
        this.debounceFlags.drag = false;
      });
    } else if (this.isResizing) {
      if (this.debounceFlags.resize) return;
      this.debounceFlags.resize = true;
      requestAnimationFrame(() => {
        this.handleResizing(e.clientX);
        this.debounceFlags.resize = false;
      });
    } else if (this.isDraggingMarker) {
      if (this.debounceFlags.drag) return;
      this.debounceFlags.drag = true;
      requestAnimationFrame(() => {
        this.handleMarkerDragging(e.clientX, e.clientY);
        this.debounceFlags.drag = false;
      });
    }

    if (this.isDragging || this.isResizing || this.isDraggingMarker) {
      this.throttledUpdateConnections();
    }
  }

  /**
   * Handle touch move during an interaction
   * @param {TouchEvent} e - Touch event
   */
  handleTouchMove(e) {
    if (e.touches.length > 1) return;
    const touch = e.touches[0];
    if (this.isDragging) this.handleDragging(touch.clientX, touch.clientY);
    else if (this.isResizing) this.handleResizing(touch.clientX);
    e.preventDefault();
  }

  /**
   * Handle block dragging with snap to grid
   * @param {number} clientX - Client X coordinate
   * @param {number} clientY - Client Y coordinate
   */
  handleDragging(clientX, clientY) {
    if (!this.draggedBlock) return;

    const deltaX = clientX - this.startX;
    const deltaY = clientY - this.startY;

    const originalState = this.initialBlockStates.get(this.draggedBlock.id);
    if (!originalState) return;

    const taskRows = document.querySelectorAll('.task-row');
    const rowHeight = taskRows[0]?.offsetHeight || 60;
    const rowMove = Math.round(deltaY / rowHeight);

    let newRowIndex;
    if (this.timeline.options.allowRowCrossing) {
      newRowIndex = originalState.rowIndex + rowMove;
      if (newRowIndex < 0) newRowIndex = 0;
      if (newRowIndex >= taskRows.length) newRowIndex = taskRows.length - 1;
    } else {
      newRowIndex = originalState.rowIndex;
    }

    const dayWidth = this.timeline.options.itemWidth;

    const snapResult = this.getSnappedPosition(deltaX);
    let effectiveDeltaX = this.timeline.options.enableSnapping ? snapResult.snappedDx : deltaX;

    let newLeft = originalState.left + effectiveDeltaX;

    const timelineWidth = this.timeline.headers.length * dayWidth;
    const blockWidth = this.draggedBlock.width || originalState.width;

    const leftLimit = -(blockWidth - dayWidth);
    if (newLeft < leftLimit) {
      newLeft = leftLimit;
    }

    const rightLimit = timelineWidth - dayWidth;
    if (newLeft > rightLimit) {
      newLeft = rightLimit;
    }

    if (this.timeline.options.preventOverlap) {
      const newStartDate = this.timeline.workingDaysManager.positionToDate(newLeft, dayWidth);
      if (newStartDate) {
        const tempBlock = {...this.draggedBlock};
        tempBlock.startDate = newStartDate;
        tempBlock.rowIndex = newRowIndex;
        tempBlock.endDate = this.timeline.workingDaysManager.calculateEndDate(tempBlock.startDate, tempBlock.duration);

        const overlappingBlock = this.timeline.dataManager.checkBlockDateOverlap(tempBlock);
        if (overlappingBlock) {
          return false;
        }
      }
    }

    this.lastMovement = {
      dx: effectiveDeltaX,
      isSnapped: snapResult.isSnapped,
      snapDistance: snapResult.snapDistance
    };

    const blockElement = document.querySelector(`.task-block[data-id="${this.draggedBlock.id}"]`);
    if (!blockElement) return;

    blockElement.style.left = `${newLeft}px`;
    blockElement.style.width = `${originalState.width}px`;
    blockElement.classList.add('dragging');

    if (newRowIndex !== this.draggedBlock.rowIndex) {
      const newTaskRow = document.querySelector(`.task-row[data-row-index="${newRowIndex}"]`);
      if (newTaskRow) {
        const newTaskTimeline = newTaskRow.querySelector('.task-timeline');
        if (newTaskTimeline && blockElement.parentNode !== newTaskTimeline) {
          newTaskTimeline.appendChild(blockElement);
        }
      }
      this.draggedBlock.rowIndex = newRowIndex;
      blockElement.dataset.rowIndex = newRowIndex;
    }

    if (this.timeline.options.snapFeedback && snapResult.isSnapped) {
      blockElement.classList.add('snapped');

      let snapIndicator = document.querySelector(`.snap-indicator[data-for="${this.draggedBlock.id}"]`);
      if (!snapIndicator) {
        snapIndicator = document.createElement('div');
        snapIndicator.className = 'snap-indicator';
        snapIndicator.dataset.for = this.draggedBlock.id;
        blockElement.parentNode.appendChild(snapIndicator);
      }

      snapIndicator.style.left = `${newLeft}px`;
      snapIndicator.style.height = `${taskRows[0]?.offsetHeight || 60}px`;

      setTimeout(() => {
        if (snapIndicator) {
          snapIndicator.style.opacity = '0';
          setTimeout(() => {
            if (snapIndicator && snapIndicator.parentNode) {
              snapIndicator.parentNode.removeChild(snapIndicator);
            }
          }, 300);
        }
      }, 500);
    } else {
      blockElement.classList.remove('snapped');
    }
  }

  /**
   * Handle marker dragging
   * @param {number} clientX - Client X coordinate
   * @param {number} clientY - Client Y coordinate
   */
  handleMarkerDragging(clientX, clientY) {
    if (!this.draggedMarker) return;

    const deltaX = clientX - this.startX;
    const deltaY = clientY - this.startY;
    const dayWidth = this.timeline.options.itemWidth;
    const markerElement = document.querySelector(`.timeline-marker[data-id="${this.draggedMarker.id}"]`);

    if (markerElement) {
      const dayMove = Math.round(deltaX / dayWidth);
      let newDay = this.initialMarkerState.day + dayMove;
      const daysInMonth = this.timeline.markerManager.getDaysInMonth();
      if (newDay < 0) newDay = 0;
      if (newDay >= daysInMonth) newDay = daysInMonth - 1;

      const newLeft = newDay * dayWidth;
      this.draggedMarker.day = newDay;
      markerElement.dataset.day = newDay;
      markerElement.style.left = `${newLeft}px`;

      if (!this.timeline.options.lockMarkerToTopRow) {
        const taskRows = document.querySelectorAll('.task-row');
        if (taskRows.length > 0) {
          const rowHeight = taskRows[0].offsetHeight;
          const rowMove = Math.round(deltaY / rowHeight);
          let newRowIndex = this.initialMarkerState.rowIndex + rowMove;

          if (newRowIndex < 0) newRowIndex = 0;
          if (newRowIndex >= taskRows.length) newRowIndex = taskRows.length - 1;

          const diamond = markerElement.querySelector('.marker-diamond');
          if (diamond) {
            const firstRowRect = taskRows[0].getBoundingClientRect();
            const firstRowTop = firstRowRect.top + 5 - this.timeline.groupHeaderHeight;
            const rowRect = taskRows[newRowIndex].getBoundingClientRect();
            const middleRow = (rowRect.bottom - rowRect.top) / 2;
            const newTop = rowRect.top - firstRowTop + middleRow;
            diamond.style.top = `${newTop}px`;
          }

          this.draggedMarker.rowIndex = newRowIndex;
          markerElement.dataset.rowIndex = newRowIndex;
        }
      }
    }
  }

  /**
   * Get the snapped position based on delta X
   * @param {number} dx - Delta X
   * @returns {Object} - Snapped delta X and snapping information
   */
  getSnappedPosition(dx) {
    const dayWidth = this.timeline.options.itemWidth;
    const daysMovedDecimal = dx / dayWidth;

    if (!this.timeline.options.enableSnapping) {
      return {
        dx: dx,
        snappedDx: dx,
        isSnapped: false,
        snapDistance: 0
      };
    }

    const snapUnit = this.timeline.options.snapUnit || 1;
    let snapThreshold = this.timeline.options.snapThreshold || 0.3;

    switch (this.timeline.options.snapStrength) {
      case 'weak':
        snapThreshold = 0.15;
        break;
      case 'strong':
        snapThreshold = 0.5;
        break;
      default:
        snapThreshold = 0.3;
    }

    const snapPoints = [];
    const baseSnapPoint = Math.round(daysMovedDecimal / snapUnit) * snapUnit;
    snapPoints.push(baseSnapPoint);

    if (this.timeline.options.snapToWeekStart) {
      const currentDay = new Date();
      const dayOfWeek = currentDay.getDay();
      const daysToWeekStart = dayOfWeek;

      const prevWeekStart = Math.floor(daysMovedDecimal / 7) * 7;
      const nextWeekStart = Math.ceil(daysMovedDecimal / 7) * 7;

      snapPoints.push(prevWeekStart);
      snapPoints.push(nextWeekStart);
    }

    let closestSnapPoint = baseSnapPoint;
    let minDistance = Math.abs(daysMovedDecimal - baseSnapPoint);

    for (const snapPoint of snapPoints) {
      const distance = Math.abs(daysMovedDecimal - snapPoint);
      if (distance < minDistance) {
        minDistance = distance;
        closestSnapPoint = snapPoint;
      }
    }

    const shouldSnap = minDistance <= snapThreshold;
    const snappedDaysMove = shouldSnap ? closestSnapPoint : daysMovedDecimal;
    const snappedDx = snappedDaysMove * dayWidth;

    return {
      dx: dx,
      snappedDx: snappedDx,
      isSnapped: shouldSnap,
      snapDistance: minDistance * dayWidth
    };
  }

  /**
   * Get a date from a day number
   * @param {number} day - Day number (0-based)
   * @returns {string|null} - Date string in YYYY-MM-DD format or null
   */
  getDateFromDay(day) {
    if (day < 0 || day >= this.timeline.headers.length) {
      return null;
    }
    return this.timeline.workingDaysManager.markerDayToDate(day);
  }

  /**
   * Handle block resizing
   * @param {number} clientX - Client X coordinate
   */
  handleResizing(clientX) {
    if (!this.resizeHandle) return;

    const deltaX = clientX - this.startX;
    const block = this.resizeHandle.block;
    const direction = this.resizeHandle.direction;

    const blockId = block.id || block.tempId;
    if (!blockId) return;

    const originalState = this.initialBlockStates.get(blockId);
    if (!originalState) {
      console.warn(this.timeline.t('Missing initial state for block:'), blockId);
      this.storeBlockInitialState(block);
      return;
    }

    const dayWidth = this.timeline.options.itemWidth;
    const blockElement = document.querySelector(`.task-block[data-id="${blockId}"]`);
    if (!blockElement) return;

    let newLeft = originalState.left;
    let newWidth = originalState.width;

    if (direction === 'right') {
      newWidth = Math.max(dayWidth, originalState.width + deltaX);
    } else if (direction === 'left') {
      const maxDeltaX = originalState.width - dayWidth;
      const limitedDeltaX = Math.min(deltaX, maxDeltaX);

      newLeft = originalState.left + limitedDeltaX;
      newWidth = originalState.width - limitedDeltaX;
    }

    const tempBlock = {...block};
    tempBlock.left = newLeft;
    tempBlock.width = newWidth;

    tempBlock.startDate = this.timeline.workingDaysManager.positionToDate(newLeft, dayWidth);
    tempBlock.startDay = this.timeline.workingDaysManager.calculateBLockIndex(tempBlock.startDate);
    tempBlock.duration = Math.max(1, Math.round(newWidth / dayWidth));
    tempBlock.endDate = this.timeline.workingDaysManager.calculateEndDate(tempBlock.startDate, tempBlock.duration);

    let isOverlapping = false;
    if (this.timeline.options.preventOverlap) {
      isOverlapping = this.timeline.dataManager.checkBlockDateOverlap(tempBlock);
    }

    if (isOverlapping) {
      blockElement.classList.add('overlapping');

      let ghostBlock = document.querySelector(`.ghost-block[data-for="${blockId}"]`);
      if (!ghostBlock) {
        ghostBlock = document.createElement('div');
        ghostBlock.className = 'ghost-block';
        ghostBlock.dataset.for = blockId;
        blockElement.parentNode.appendChild(ghostBlock);
      }

      ghostBlock.style.left = `${newLeft}px`;
      ghostBlock.style.width = `${newWidth}px`;
      ghostBlock.style.top = `${blockElement.offsetTop}px`;
      ghostBlock.style.height = `${blockElement.offsetHeight}px`;
      ghostBlock.style.backgroundColor = 'rgba(255, 77, 77, 0.3)';
      ghostBlock.style.display = 'block';
    } else {
      blockElement.classList.remove('overlapping');

      const ghostBlock = document.querySelector(`.ghost-block[data-for="${blockId}"]`);
      if (ghostBlock) {
        ghostBlock.remove();
      }

      blockElement.style.left = `${newLeft}px`;
      blockElement.style.width = `${newWidth}px`;

      const progressBar = blockElement.querySelector('.task-progress');
      if (progressBar && block.progress !== undefined) {
        progressBar.style.width = `${block.progress}%`;
      }
    }
  }

  /**
   * Debounce a function
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} - Debounced function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Throttle a function
   * @param {Function} func - Function to throttle
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} - Throttled function
   */
  throttle(func, wait) {
    let lastCall = 0;
    return function(...args) {
      const now = Date.now();
      if (now - lastCall >= wait) {
        lastCall = now;
        func.apply(this, args);
      }
    };
  }

  /**
   * Handle mouse up at the end of an interaction
   * @param {MouseEvent} e - Mouse event
   */
  handleMouseUp(e) {
    document.removeEventListener('mousemove', this.mouseMoveHandler);
    document.removeEventListener('mouseup', this.mouseUpHandler);
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.mouseUpHandler);
    document.body.style.cursor = '';

    if (this.isDragging) {
      this.finalizeBlockDragging();
    } else if (this.isResizing) {
      this.finalizeBlockResizing();
    } else if (this.isDraggingMarker) {
      this.finalizeMarkerDragging();
    } else {
      if (this.draggedBlock) {
        const blockElement = document.querySelector(`.task-block[data-id="${this.draggedBlock.id}"]`);
        if (blockElement) {
          if (blockElement.dataset.originalVisibleWidth && blockElement.dataset.originalVisibleLeft) {
            blockElement.style.width = blockElement.dataset.originalVisibleWidth;
            blockElement.style.left = blockElement.dataset.originalVisibleLeft;
          }
          blockElement.classList.remove('dragging');
        }
      }
    }

    this.isDragging = false;
    this.isResizing = false;
    this.isDraggingMarker = false;
    this.draggedBlock = null;
    this.draggedMarker = null;
    this.resizeHandle = null;
    this.initialBlockStates.clear();
    this.initialMarkerState = null;

    this.timeline.connectionManager.updateConnections();
    this.timeline.triggerDataChangeCallback();
    this.timeline.trigger('blocksUpdated', {});
  }

  /**
   * Finalize block dragging with proper server-side handling and snap to grid
   */
  async finalizeBlockDragging() {
    if (!this.draggedBlock) return;

    const blockElement = document.querySelector(`.task-block[data-id="${this.draggedBlock.id}"]`);
    if (!blockElement) return;

    blockElement.classList.remove('dragging');
    blockElement.classList.remove('snapped');
    blockElement.style.transform = '';

    const snapIndicator = document.querySelector(`.snap-indicator[data-for="${this.draggedBlock.id}"]`);
    if (snapIndicator && snapIndicator.parentNode) {
      snapIndicator.parentNode.removeChild(snapIndicator);
    }

    const dayWidth = this.timeline.options.itemWidth;
    const currentLeft = parseInt(blockElement.style.left) || 0;
    const currentRowIndex = parseInt(blockElement.dataset.rowIndex) || 0;

    let snappedLeft = currentLeft;

    if (this.timeline.options.enableSnapping) {
      const originalState = this.initialBlockStates.get(this.draggedBlock.id);
      if (originalState) {
        const deltaX = currentLeft - originalState.left;
        const snapResult = this.getSnappedPosition(deltaX);

        if (snapResult.isSnapped) {
          snappedLeft = originalState.left + snapResult.snappedDx;
        }
      }

      const approximateDate = this.timeline.workingDaysManager.positionToDate(snappedLeft, dayWidth);
      if (approximateDate) {
        if (this.timeline.options.snapToWeekStart) {
          const dayOfWeek = approximateDate.getDay();
          if (dayOfWeek !== 0) {
            const daysToLastSunday = dayOfWeek;
            const daysToNextSunday = 7 - dayOfWeek;

            const snapThreshold = this.timeline.options.snapThreshold || 0.3;
            if (daysToLastSunday <= snapThreshold * 7) {
              const lastSunday = new Date(approximateDate);
              lastSunday.setDate(lastSunday.getDate() - daysToLastSunday);
              snappedLeft = this.timeline.workingDaysManager.dateToPosition(lastSunday, dayWidth);
            } else if (daysToNextSunday <= snapThreshold * 7) {
              const nextSunday = new Date(approximateDate);
              nextSunday.setDate(nextSunday.getDate() + daysToNextSunday);
              snappedLeft = this.timeline.workingDaysManager.dateToPosition(nextSunday, dayWidth);
            }
          }
        } else {
          snappedLeft = this.timeline.workingDaysManager.dateToPosition(approximateDate, dayWidth);
        }
      }
    }

    blockElement.style.left = `${snappedLeft}px`;

    const finalData = this.calculateFinalBlockDataFromSnappedPosition(snappedLeft, currentRowIndex);

    const hasChanges = this.hasBlockChanged(this.draggedBlock, finalData);

    if (!hasChanges) {
      this.timeline.domRenderer.renderBlock(this.draggedBlock, blockElement);
      return;
    }

    Object.assign(this.draggedBlock, finalData);
    this.timeline.dataManager.updateBlockDates(this.draggedBlock);

    if (this.timeline.options.useServerSideConnections) {
      await this.handleServerSideDrag(finalData);
    } else {
      await this.handleClientSideDrag(finalData);
    }

    this.timeline.triggerCallback('onBlockUpdate', this.draggedBlock);
    this.timeline.triggerDataChangeCallback();
  }

  /**
   * Enhanced calculateFinalBlockData with better snap support
   * @param {HTMLElement} blockElement - Block DOM element
   * @returns {Object} - Final block data
   */
  calculateFinalBlockData(blockElement) {
    const dayWidth = this.timeline.options.itemWidth;
    const currentLeft = parseInt(blockElement.style.left) || 0;
    const currentRowIndex = parseInt(blockElement.dataset.rowIndex) || 0;

    let snappedLeft = currentLeft;
    if (this.timeline.workingDaysManager && this.timeline.workingDaysManager.initialized) {
      const approximateDate = this.timeline.workingDaysManager.positionToDate(currentLeft, dayWidth);
      if (approximateDate) {
        snappedLeft = this.timeline.workingDaysManager.dateToPosition(approximateDate, dayWidth);
      }
    } else {
      snappedLeft = Math.round(currentLeft / dayWidth) * dayWidth;
    }

    blockElement.style.left = `${snappedLeft}px`;

    return this.calculateFinalBlockDataFromSnappedPosition(snappedLeft, currentRowIndex);
  }

  /**
   * Calculate final block data from snapped position
   * @param {number} snappedLeft - Snapped left position
   * @param {number} rowIndex - Row index
   * @returns {Object} - Final block data
   */
  calculateFinalBlockDataFromSnappedPosition(snappedLeft, rowIndex) {
    const dayWidth = this.timeline.options.itemWidth;

    snappedLeft = Math.round(snappedLeft);

    let finalData = {
      left: snappedLeft,
      rowIndex: rowIndex,
      duration: this.draggedBlock.duration
    };

    finalData.startDate = this.timeline.workingDaysManager.positionToDate(snappedLeft, dayWidth);

    if (finalData.startDate) {
      finalData.startDay = this.timeline.workingDaysManager.dateToIndex(finalData.startDate);
    } else {
      finalData.startDay = this.draggedBlock.startDay || 0;
    }

    finalData.endDate = this.timeline.workingDaysManager.calculateEndDate(
      finalData.startDate,
      this.draggedBlock.duration
    );

    const originalState = this.initialBlockStates.get(this.draggedBlock.id);
    if (originalState) {
      finalData.dayChange = finalData.startDay - originalState.startDay;
    }

    return finalData;
  }

  /**
   * Handle drag completion with client-side connection management
   */
  async handleClientSideDrag(finalData) {
    try {
      const blockElement = document.querySelector(`.task-block[data-id="${this.draggedBlock.id}"]`);
      this.timeline.domRenderer.renderBlock(this.draggedBlock, blockElement);

      if (this.timeline.options.syncConnectedBlocks) {
        const originalState = this.initialBlockStates.get(this.draggedBlock.id);
        if (originalState && finalData) {
          const dayChange = finalData.startDay - originalState.startDay;
          if (dayChange !== 0) {
            this.timeline.connectionManager.syncConnectedBlocks(
              this.draggedBlock,
              dayChange
            );
          }
        }
      }

      this.timeline.connectionManager.updateConnections();

      await this.timeline.dataManager.saveDataToServer();
    } catch (error) {
      console.error('Error in client-side drag handling:', error);
      this.timeline.showNotification(
        this.timeline.t('Error updating block position'),
        'error'
      );
    }
  }

  /**
   * Handle drag completion with server-side connection management
   * @param {Object} finalData - Final calculated data for dragged block
   */
  async handleServerSideDrag(finalData) {
    try {
      const blockData = {
        id: this.draggedBlock.id,
        tempId: this.draggedBlock.tempId,
        row_index: this.draggedBlock.rowIndex,
        start_date: this.draggedBlock.startDate,
        duration: this.draggedBlock.duration,
        color: this.draggedBlock.color,
        display_text: this.draggedBlock.displayText,
        icon: this.draggedBlock.icon,
        progress: this.draggedBlock.progress || 0
      };

      const blockElement = document.querySelector(`.task-block[data-id="${this.draggedBlock.id}"]`);
      if (blockElement) {
        this.timeline.domRenderer.renderBlock(this.draggedBlock, blockElement);
      }

      const result = await this.timeline.sendBlockUpdateToAPI(blockData);

      if (!result.success) {
        console.error('Error in server-side drag handling:', result.error);
        this.timeline.showNotification(
          this.timeline.t('Error updating block position: {error}', {error: result.error || 'Unknown error'}),
          'error'
        );

        const originalState = this.initialBlockStates.get(this.draggedBlock.id);
        if (originalState && blockElement) {
          Object.assign(this.draggedBlock, {
            left: originalState.left,
            width: originalState.width,
            rowIndex: originalState.rowIndex,
            startDay: originalState.startDay,
            startDate: originalState.startDate,
            endDate: originalState.endDate,
            duration: originalState.duration
          });

          this.timeline.domRenderer.renderBlock(this.draggedBlock, blockElement);
          this.timeline.connectionManager.updateConnections();
        }
      }
    } catch (error) {
      console.error('Error in server-side drag handling:', error);
      this.timeline.showNotification(
        this.timeline.t('Error updating block position'),
        'error'
      );
    }
  }

  /**
   * Save changed blocks to server with enhanced connection handling
   */
  async saveChangedBlocks(changedBlocks, projectId, settings = {}) {
    try {
      let currentConnections = [];
      if (this.timeline.dependencyManager && typeof this.timeline.dependencyManager.getAllConnections === 'function') {
        currentConnections = this.timeline.dependencyManager.getAllConnections();
      } else if (this.timeline.dependencyManager && this.timeline.dependencyManager.connections) {
        currentConnections = this.timeline.dependencyManager.connections;
      }

      const requestData = {
        projectId: projectId || this.timeline.currentProject?.id || 1,
        blocks: changedBlocks,
        settings: {
          showHolidays: settings.showHolidays || false,
          preventOverlap: settings.preventOverlap || true,
          syncConnectedBlocks: settings.syncConnectedBlocks || false,
          useServerSideConnections: settings.useServerSideConnections || false,
          allowRowCrossing: settings.allowRowCrossing || true,
          calculateConnectedBlocks: settings.calculateConnectedBlocks || false,

          connections: currentConnections.map(conn => ({
            id: conn.id,
            tempId: conn.id || conn.tempId,
            fromId: conn.fromId,
            toId: conn.toId,
            type: conn.type || 'default',
            gap: conn.gap || 1
          }))
        }
      };

      const result = await this.sendRequest('update_blocks', 'POST', requestData);

      if (result.success) {
        return {
          success: true,
          message: 'บันทึกบล็อกทั้งหมดสำเร็จ',
          data: {
            updatedBlocks: result.data.updatedBlocks || [],
            updatedConnections: result.data.updatedConnections || [],
            affectedBlocks: result.data.affectedBlocks || [],
            serverCalculated: result.data.serverCalculated || false
          }
        };
      } else {
        console.error('เกิดข้อผิดพลาดในการอัปเดตกลุ่มบล็อก:', result.error);
        return {
          success: false,
          error: result.error,
          message: 'เกิดข้อผิดพลาดจากเซิร์ฟเวอร์'
        };
      }
    } catch (error) {
      console.error('เกิดข้อผิดพลาดในการส่งข้อมูลไปยัง API:', error);
      return {
        success: false,
        error: error.message,
        message: 'เกิดข้อผิดพลาดในการส่งข้อมูล'
      };
    }
  }

  /**
   * Finalize block resizing
   */
  async finalizeBlockResizing() {
    if (!this.resizeHandle) return;

    const block = this.resizeHandle.block;
    const blockId = block.id || block.tempId;
    const direction = this.resizeHandle.direction;

    if (!blockId) {
      console.warn(this.timeline.t('Block has no ID or tempId in finalizeBlockResizing'));
      return;
    }

    const blockElement = document.querySelector(`.task-block[data-id="${blockId}"]`);
    if (!blockElement) return;

    blockElement.classList.remove('resizing', 'overlapping');
    const ghostBlock = document.querySelector(`.ghost-block[data-for="${blockId}"]`);
    if (ghostBlock) {
      ghostBlock.remove();
    }

    const originalState = this.initialBlockStates.get(blockId);
    let isOverlapping = false;
    const dayWidth = this.timeline.options.itemWidth;
    const currentLeft = parseInt(blockElement.style.left);
    const currentWidth = parseInt(blockElement.style.width);

    if (this.timeline.options.preventOverlap) {
      const tempBlock = {...block};
      tempBlock.left = currentLeft;
      tempBlock.width = currentWidth;

      tempBlock.startDate = this.timeline.workingDaysManager.positionToDate(currentLeft, dayWidth);
      tempBlock.startDay = this.timeline.workingDaysManager.calculateBLockIndex(tempBlock.startDate);
      tempBlock.duration = Math.max(1, Math.round(currentWidth / dayWidth));
      tempBlock.endDate = this.timeline.workingDaysManager.calculateEndDate(tempBlock.startDate, tempBlock.duration);

      isOverlapping = this.timeline.dataManager.checkBlockDateOverlap(tempBlock);
    }

    if (isOverlapping && originalState) {
      block.left = originalState.left;
      block.width = originalState.width;
      block.startDay = originalState.startDay;
      block.duration = originalState.duration;
      block.startDate = originalState.startDate;
      block.endDate = originalState.endDate;

      blockElement.style.left = `${originalState.left}px`;
      blockElement.style.width = `${originalState.width}px`;

      this.timeline.showNotification(this.timeline.t('Cannot resize block: Would overlap with another block'), 'error');
    } else {
      const snappedStartDate = this.timeline.workingDaysManager.positionToDate(currentLeft, dayWidth);
      const snappedStartDay = this.timeline.workingDaysManager.calculateBLockIndex(snappedStartDate);
      const snappedDuration = Math.max(1, Math.round(currentWidth / dayWidth));

      const snappedLeft = this.timeline.workingDaysManager.dateToPosition(snappedStartDate, dayWidth);
      const snappedWidth = snappedDuration * dayWidth;

      block.left = snappedLeft;
      block.width = snappedWidth;
      block.startDay = snappedStartDay;
      block.duration = snappedDuration;
      block.startDate = snappedStartDate;
      block.endDate = this.timeline.workingDaysManager.calculateEndDate(snappedStartDate, snappedDuration);

      blockElement.style.left = `${block.left}px`;
      blockElement.style.width = `${block.width}px`;

      if (direction === 'right') {
        this.updateConnectedBlocksAfterResize(block);
      } else if (direction === 'left') {
        const incomingConnections = this.timeline.connectionManager.getConnectionsToBlock(block.id);
        incomingConnections.forEach(connection => {
          const sourceBlock = this.timeline.dataManager.blocks.find(b => b.id === connection.fromId);
          if (!sourceBlock) return;

          connection.gap = this.timeline.connectionManager.calculateGapBetweenBlocks(sourceBlock, block);
        });
      }
    }

    document.body.style.cursor = '';

    this.timeline.dataManager.updateBlockDates(block);
    this.timeline.dataManager.saveDataToServer();
    this.timeline.triggerCallback('onBlockUpdate', block);
    this.timeline.triggerDataChangeCallback();

    if (this.timeline.options.useServerSideConnections && !this.timeline.options.syncConnectedBlocks) {
      const blockData = {
        id: this.draggedBlock.id,
        tempId: this.draggedBlock.tempId,
        row_index: this.draggedBlock.rowIndex,
        start_date: this.draggedBlock.startDate,
        duration: this.draggedBlock.duration,
        color: this.draggedBlock.color,
        display_text: this.draggedBlock.displayText,
        icon: this.draggedBlock.icon,
        progress: this.draggedBlock.progress || 0
      };

      const result = await this.timeline.sendBlockUpdateToAPI(blockData);
    }
  }

  /**
   * Update connected blocks after resizing a block
   * @param {Object} sourceBlock - Source block that was resized
   */
  updateConnectedBlocksAfterResize(sourceBlock) {
    if (!this.timeline.options.syncConnectedBlocks) return;

    const connections = this.timeline.connectionManager.getConnectionsFromBlock(sourceBlock.id);
    if (!connections || connections.length === 0) return;

    const updatedBlocks = new Map();
    updatedBlocks.set(sourceBlock.id, true);

    for (const connection of connections) {
      const dependentId = connection.toId;

      if (updatedBlocks.has(dependentId)) continue;

      const block = this.timeline.dataManager.blocks.find(b => b.id === dependentId);
      if (!block) continue;

      const sourceEndDate = this.timeline.workingDaysManager.calculateEndDate(
        sourceBlock.startDate,
        sourceBlock.duration
      );

      if (!sourceEndDate) continue;

      const gap = connection.gap !== undefined ? connection.gap : 1;

      const finalStartDate = this.timeline.workingDaysManager.addDays(sourceEndDate, gap);

      block.startDate = finalStartDate;
      block.startDay = this.timeline.workingDaysManager.calculateBLockIndex(finalStartDate);
      block.left = this.timeline.workingDaysManager.dateToPosition(finalStartDate, this.timeline.options.itemWidth);

      this.timeline.dataManager.updateBlockDates(block);

      const blockElement = document.querySelector(`.task-block[data-id="${block.id}"]`);
      if (blockElement) {
        this.timeline.domRenderer.renderBlock(block, blockElement);
      }

      updatedBlocks.set(block.id, true);

      this.updateConnectedBlocksAfterResize(block);

      connection.gap = this.timeline.connectionManager.calculateGapBetweenBlocks(sourceBlock, block);
    }
  }

  /**
   * Finalize marker dragging
   */
  async finalizeMarkerDragging() {
    if (!this.draggedMarker) return;

    const markerElement = document.querySelector(`.timeline-marker[data-id="${this.draggedMarker.id || this.draggedMarker.tempId}"]`);
    if (markerElement) {
      markerElement.classList.remove('dragging');
    }

    const originalMarker = this.timeline.dataManager.markers.find(
      (m) => (this.draggedMarker.id && m.id === this.draggedMarker.id) ||
        (this.draggedMarker.tempId && m.tempId === this.draggedMarker.tempId)
    );

    if (originalMarker &&
      (originalMarker.day !== this.draggedMarker.day ||
        originalMarker.rowIndex !== this.draggedMarker.rowIndex)) {
      originalMarker.day = this.draggedMarker.day;
      originalMarker.rowIndex = this.draggedMarker.rowIndex;

      const result = await this.timeline.markerManager.sendMarkerToAPI(originalMarker);

      if (result.success) {
        this.timeline.showNotification(this.timeline.t('Marker updated successfully'), 'success');
      } else {
        this.timeline.showNotification(
          this.timeline.t('Failed to update marker on server: {message}', {message: result.error || 'Unknown error'}),
          'error'
        );
      }

      this.timeline.triggerCallback('onMarkerMove', originalMarker);
      this.timeline.triggerDataChangeCallback();
    }
  }

  /**
   * Get task information for a row
   * @param {number} rowIndex - Row index
   * @returns {Object|null} - Task info or null
   */
  getTaskInfo(rowIndex) {
    let currentRowIndex = 0;
    for (const group of this.timeline.dataManager.taskGroups) {
      for (const task of group.tasks) {
        if (currentRowIndex === rowIndex) return task;
        currentRowIndex++;
      }
    }
    return null;
  }

  /**
   * Check if block has changed from its initial state
   * @param {Object} block - Block object
   * @param {Object} finalData - Final calculated data
   * @returns {boolean} - True if block has changed
   */
  hasBlockChanged(block, finalData) {
    const originalState = this.initialBlockStates.get(block.id);
    if (!originalState) return true;

    const leftTolerance = 2;
    const leftChanged = Math.abs(originalState.left - finalData.left) > leftTolerance;

    const rowChanged = originalState.rowIndex !== finalData.rowIndex;
    const startDateChanged = originalState.startDate !== finalData.startDate;
    const durationChanged = originalState.duration !== finalData.duration;

    return leftChanged || rowChanged || startDateChanged || durationChanged;
  }

  /**
   * Save block changes to server
   * @param {Object} block - Block to save
   */
  async saveBlockChanges(block) {
    this.timeline.dataManager.markBlockAsChanged(block);

    if (this.timeline.options.useServerSideConnections && !this.timeline.options.syncConnectedBlocks) {
      const blockData = {
        id: block.id,
        tempId: block.tempId,
        row_index: block.rowIndex,
        start_date: block.startDate,
        duration: block.duration,
        color: block.color,
        display_text: block.displayText,
        icon: block.icon,
        progress: block.progress || 0
      };

      await this.timeline.sendBlockUpdateToAPI(blockData);
    } else {
      this.timeline.dataManager.saveDataToServer();
    }
  }
}

window.EventHandler = EventHandler;
